import React, { useEffect, useState } from 'react'
import '../common.css'
import AppDataGrid from '../../global/AppDataGrid'
import AppPageHeader from '../../global/AppPageHeader'
import BrandFilter from '../brandFilter/BrandFilter'
import RooftopPriceActions from '../priceActions/RooftopPriceActions'
import Header from '../../global/GridFilterHeader'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import { filterDataByBrand, hasActiveFilters } from '../utils/filters'

import { apiUrl } from '../../../environments/environments'
import Warning from '@mui/icons-material/Warning'
import useGridRowDeletion from '../../global/useGridRowDeletion'
import { useIsProjectEditor } from '../../../hooks/useUserPermissions'

const getRowId = (row) => `${row.codeModel}-${row.config}-${row.airFlow}-${row.varcond}-${row.price}`

function AirFlowsPage() {
    const [data, setData] = useState([])
    const [dataToDisplay, setDataToDisplay] = useState([])
    const [brandFilter, setBrandFilter] = useState('All')
    const [loading, setLoading] = useState(true)
    const [notice, setNotice] = useState(null)
    const [filters, setFilters] = useState({})
    const isProjectEditor = useIsProjectEditor()

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }))
    }

    const refreshData = () => {
        setLoading(true)
        ApiService(`${apiUrl}/rooftop/airFlows/`).then(({ status, data }) => {
            if (status === 200 && data) {
                setData(data.result)
            }
            setLoading(false)
        })
    }

    // Get data from database
    useEffect(() => {
        refreshData()
    }, [])

    // filter font side on brand
    useEffect(() => {
        let filteredData = filterDataByBrand(data, brandFilter)

        const isEmpty = (v) => v == null || v === ''

        const isNumber = (raw) => {
            const s = String(raw).trim()
            return /^-?\d+(\.\d+)?$/.test(s) // -1806, 1806, -12.5, 12.5
        }

        // Optionnel mais recommandé: enlève espaces/devise + normalise tirets unicode
        const normalizePrice = (v) =>
            String(v ?? '')
                .trim()
                .replace(/\u2212|\u2013|\u2014/g, '-') // − – — => -
                .replace(/\s+/g, '') // supprime espaces
                .replace(/[€$,]/g, '') // supprime symboles courants (si tu en as)

        const isNegativePriceString = (v) => {
            const p = normalizePrice(v)
            // vrai négatif = contient "-<digit>" (ex: -1806, -0.5)
            return /-\d/.test(p)
        }

        Object.entries(filters).forEach(([field, value]) => {
            const raw = value == null ? '' : String(value).trim()

            // ✅ vide => pas de filtre (retour arrière)
            if (raw === '') return
            filteredData = filteredData.filter((row) => {
                const fieldValue = row[field]

                // ----- PRICE LOGIC -----
                if (field === 'price') {
                    // "-" seul => afficher les prix négatifs
                    if (raw === '-') {
                        return isNegativePriceString(fieldValue)
                    }

                    // nombre valide => filtre normal (sur la string)
                    if (isNumber(raw)) {
                        return normalizePrice(fieldValue).includes(normalizePrice(raw))
                    }

                    // texte non numérique => no price
                    return isEmpty(fieldValue)
                }

                // ----- OTHER COLUMNS -----
                if (isEmpty(fieldValue)) return false
                return String(fieldValue).toLowerCase().includes(raw.toLowerCase())
            })
        })

        setDataToDisplay(filteredData)
    }, [brandFilter, data, filters])

    const uploadStart = () => {
        setNotice(null)
        setLoading(true)
    }

    const uploadSuccess = (rows, notice) => {
        setData(rows)
        setNotice(notice)
        setLoading(false)
    }

    const uploadError = (message) => {
        setLoading(false)
        setNotice({ severity: 'error', message: `Import failed: ${message || 'unknown error'}` })
    }
    const columns = [
        {
            field: 'codeModel',
            headerName: 'Model',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Model'
                    onFilter={handleFilterChange}
                    field='codeModel'
                    filterValue={filters.codeModel}
                    id='filter-airflows-model'
                />
            ),
        },
        {
            field: 'config',
            headerName: 'Assembly',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Assembly'
                    onFilter={handleFilterChange}
                    field='config'
                    filterValue={filters.config}
                    id='filter-airflows-assembly'
                />
            ),
        },
        {
            field: 'airFlow',
            headerName: 'AirFlow',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='AirFlow'
                    onFilter={handleFilterChange}
                    field='airFlow'
                    filterValue={filters.airFlow}
                    id='filter-airflows-airflow'
                />
            ),
        },
        {
            field: 'oldPrice',
            headerName: 'Old Price',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Old Price'
                    onFilter={handleFilterChange}
                    field='oldPrice'
                    filterValue={filters.oldPrice}
                    id='filter-airflows-oldprice'
                />
            ),
        },
        {
            field: 'varcond',
            headerName: 'Varconds',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Varconds'
                    onFilter={handleFilterChange}
                    field='varcond'
                    filterValue={filters.varcond}
                    id='filter-airflows-varconds'
                />
            ),
        },
        {
            field: 'kmat',
            headerName: 'Kmat',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Kmat'
                    onFilter={handleFilterChange}
                    field='kmat'
                    filterValue={filters.kmat}
                    id='filter-airflows-kmat'
                />
            ),
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                if (params.value === undefined || params.value === null) {
                    return (
                        <span style={{ color: 'red', background: 'white' }}>
                            <Warning color='error' className='warningIcon'></Warning> No Price
                        </span>
                    )
                }
                return params.value
            },
            renderHeader: () => (
                <Header
                    title='Price'
                    onFilter={handleFilterChange}
                    field='price'
                    filterValue={filters.price}
                    id='filter-airflows-price'
                />
            ),
        },
    ]

    const deletion = useGridRowDeletion({
        enabled: isProjectEditor,
        rows: dataToDisplay,
        getRowId,
        url: `${apiUrl}/rooftop/airFlows/`,
        tableLabel: 'AirFlows prices',
        filtered: hasActiveFilters(brandFilter, filters),
        onDeleted: (ids) => setData((previous) => previous.filter((row) => !ids.has(row.id))),
    })

    return (
        <div className='page-container'>
            <AppPageHeader
                title='AirFlows prices'
                actionsLabel='AirFlows prices actions'
                notice={notice}
                onCloseNotice={() => setNotice(null)}
                actions={
                    isProjectEditor && (
                        <RooftopPriceActions
                            tableLabel='AirFlows prices'
                            api='/rooftop/airFlows/'
                            exportId='download-prices-airflows'
                            data={data}
                            columns={columns}
                            fileName='AirFlows_Prices.xlsx'
                            sheetName='AirFlows_Prices'
                            onImportStart={uploadStart}
                            onImportSuccess={uploadSuccess}
                            onImportError={uploadError}
                            onSyncComplete={refreshData}
                            onSyncError={(message) => setNotice(message ? { severity: 'error', message } : null)}
                        />
                    )
                }
            />
            <div className='table-container'>
                <AppDataGrid
                    showToolbar
                    toolbarRight={<BrandFilter value={brandFilter} onChange={setBrandFilter} />}
                    autoHeight={false}
                    rows={dataToDisplay}
                    columns={deletion.withActionsColumn(columns)}
                    getRowId={getRowId}
                    loading={loading}
                    pageSizeOptions={[5, 10, 20]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    rowHeight={70}
                    {...deletion.gridProps}
                />
                {deletion.dialog}
            </div>
        </div>
    )
}

export default AirFlowsPage
